import React from 'react';
import {
  FileSpreadsheet,
  FileText,
  Image,
  Link2,
  Presentation,
} from 'lucide-react';
import type { DocumentCategory, LibraryDocument, LibraryDocumentKind } from '@/services/api/documentService';

export const ALL_FOLDERS_ID = '__all__';

export type FileTypeFilter = 'all' | 'pdf' | 'word' | 'excel' | 'image' | 'google_doc' | 'google_sheet' | 'drive_link';

export function flattenCategoryTree(categories: DocumentCategory[]): { cat: DocumentCategory; depth: number }[] {
  const byParent = new Map<string | null, DocumentCategory[]>();
  const ids = new Set(categories.map((c) => c.id));
  for (const cat of categories) {
    const parentId = cat.parentId && ids.has(cat.parentId) ? cat.parentId : null;
    const list = byParent.get(parentId) || [];
    list.push(cat);
    byParent.set(parentId, list);
  }
  const sortSiblings = (items: DocumentCategory[]) =>
    items.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  const out: { cat: DocumentCategory; depth: number }[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const cat of sortSiblings(byParent.get(parentId) || [])) {
      out.push({ cat, depth });
      walk(cat.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

export function errorMessage(err: unknown): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    || (err instanceof Error ? err.message : 'Something went wrong')
  );
}

export function kindLabel(kind: LibraryDocumentKind, mimeType?: string): string {
  if (kind === 'google_doc') return 'Google Doc';
  if (kind === 'google_sheet') return 'Google Sheet';
  if (kind === 'drive_link') return 'Drive link';
  const mime = (mimeType || '').toLowerCase();
  if (mime.includes('pdf')) return 'PDF';
  if (mime.includes('spreadsheet') || mime.includes('excel')) return 'Excel';
  if (mime.includes('word') || mime.includes('document')) return 'Word';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'PowerPoint';
  if (mime.startsWith('image/')) return 'Image';
  return 'File';
}

export function fileKindKey(doc: LibraryDocument): FileTypeFilter {
  if (doc.kind === 'google_doc' || doc.kind === 'google_sheet' || doc.kind === 'drive_link') return doc.kind;
  const mime = (doc.mimeType || '').toLowerCase();
  const name = doc.name.toLowerCase();
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
  if (mime.includes('spreadsheet') || mime.includes('excel') || /\.xlsx?$/.test(name)) return 'excel';
  if (mime.includes('word') || mime.includes('document') || /\.docx?$/.test(name)) return 'word';
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/.test(name)) return 'image';
  return 'all';
}

export function FileTypeIcon({ doc, size = 22 }: { doc: LibraryDocument; size?: number }) {
  const key = fileKindKey(doc);
  if (key === 'google_sheet' || key === 'excel') {
    return <FileSpreadsheet size={size} color="#16a34a" />;
  }
  if (key === 'google_doc' || key === 'word') {
    return <FileText size={size} color="#2563eb" />;
  }
  if (key === 'pdf') {
    return <FileText size={size} color="#ef4444" />;
  }
  if (key === 'image') {
    return <Image size={size} color="#0288d1" />;
  }
  if ((doc.mimeType || '').includes('presentation') || /\.pptx?$/i.test(doc.name)) {
    return <Presentation size={size} color="#ea580c" />;
  }
  if (key === 'drive_link') {
    return <Link2 size={size} color="#64748b" />;
  }
  return <FileText size={size} color="#6b6e73" />;
}

export function formatFileSize(bytes?: number): string {
  if (bytes == null || Number.isNaN(bytes) || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function folderNameById(categories: DocumentCategory[], id: string): string {
  return categories.find((c) => c.id === id)?.name || 'Folder';
}

/** True when this folder and every descendant have zero library files. */
export function isFolderSubtreeEmpty(categories: DocumentCategory[], folderId: string): boolean {
  const ids = new Set(categories.map((c) => c.id));
  if (!ids.has(folderId)) return false;
  const children = new Map<string, string[]>();
  for (const cat of categories) {
    if (cat.parentId && ids.has(cat.parentId)) {
      const list = children.get(cat.parentId) || [];
      list.push(cat.id);
      children.set(cat.parentId, list);
    }
  }
  const stack = [folderId];
  while (stack.length) {
    const id = stack.pop()!;
    const cat = categories.find((c) => c.id === id);
    if ((cat?.documentCount || 0) > 0) return false;
    for (const childId of children.get(id) || []) stack.push(childId);
  }
  return true;
}

export type DocScopeFilter = 'all' | 'recent' | 'favorites';
export type DocSortKey = 'name' | 'size' | 'createdByName' | 'createdAt';
export type DocSortDir = 'asc' | 'desc';

const RECENT_KEY = 'nesttask:document-recent';
const RECENT_MAX = 30;

export function getRecentDocumentIds(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { id: string; at: number }[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x) => x && typeof x.id === 'string')
      .sort((a, b) => b.at - a.at)
      .map((x) => x.id);
  } catch {
    return [];
  }
}

export function pushRecentDocument(id: string): void {
  try {
    const now = Date.now();
    const existing = getRecentDocumentIds()
      .filter((x) => x !== id)
      .map((docId) => ({ id: docId, at: now - 1 }));
    const next = [{ id, at: now }, ...existing].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
}

/** Drive preview embed URL (works for Docs/Sheets/PDF/images when shared). */
export function drivePreviewUrl(doc: LibraryDocument): string {
  const id = doc.driveFileId;
  if (!id) return doc.webViewUrl;
  if (doc.kind === 'google_doc') {
    return `https://docs.google.com/document/d/${id}/preview`;
  }
  if (doc.kind === 'google_sheet') {
    return `https://docs.google.com/spreadsheets/d/${id}/preview`;
  }
  return `https://drive.google.com/file/d/${id}/preview`;
}

export function activityLabel(action: string): string {
  const map: Record<string, string> = {
    created: 'Added file',
    deleted: 'Deleted file',
    moved: 'Moved file',
    favorited: 'Starred',
    unfavorited: 'Unstarred',
    folder_created: 'Created folder',
    folder_renamed: 'Renamed folder',
    folder_hidden: 'Hid folder',
    folder_restored: 'Unhid folder',
  };
  return map[action] || action;
}
